"use client";

import { motion, stagger, useAnimate, useInView } from "motion/react";
import * as React from "react";
import { cn } from "@/lib/utils";

// Parse a words string with Markdown-style `**emphasis**` into a flat
// array of tokens: [{ text, highlight }, ...]. Emphasized runs render in
// a darker color for Apple-style inline emphasis.
//
// Trailing punctuation (`.,!?;:`) *after* a closing `**` is absorbed onto
// the last emphasized word so the comma/period hugs the word instead of
// getting its own 0.25em-spaced inline-block.
function parseTokens(input) {
  const tokens = [];
  const regex = /\*\*([\s\S]+?)\*\*([.,!?;:)\]]*)|\S+/g;
  let match;
  while ((match = regex.exec(input)) !== null) {
    if (match[1] !== undefined) {
      const words = match[1]
        .split(/\s+/)
        .filter(Boolean);
      const trailing = match[2] || "";
      words.forEach((w, i) => {
        const isLast = i === words.length - 1;
        tokens.push({
          text: isLast && trailing ? `${w}${trailing}` : w,
          highlight: true,
        });
      });
    } else {
      tokens.push({ text: match[0], highlight: false });
    }
  }
  return tokens;
}

const TextGenerateEffect = React.forwardRef(function TextGenerateEffect(
  {
    words,
    className,
    filter = true,
    duration = 0.9,
    staggerDelay = 0.08,
    as: Tag = "div",
    highlightColor = "#1d1d1f",
    trigger,
    ...props
  },
  ref
) {
  const localRef = React.useRef(null);
  React.useImperativeHandle(ref, () => localRef.current);

  const [scope, animate] = useAnimate();
  // Fallback to useInView if no `trigger` prop is supplied. When a caller
  // manages its own trigger (e.g. a card stack that only animates the
  // currently-active card), set trigger to a boolean and we ignore useInView.
  const isInView = useInView(scope, { amount: 0.4 });
  const shouldPlay = trigger === undefined ? isInView : Boolean(trigger);
  const tokens = React.useMemo(() => parseTokens(words), [words]);

  React.useEffect(() => {
    if (!scope.current) return;
    if (shouldPlay) {
      animate(
        "span",
        {
          opacity: 1,
          filter: filter ? "blur(0px)" : "none",
        },
        {
          duration,
          delay: stagger(staggerDelay),
        }
      );
    } else {
      animate(
        "span",
        {
          opacity: 0,
          filter: filter ? "blur(10px)" : "none",
        },
        { duration: 0 }
      );
    }
  }, [animate, duration, filter, shouldPlay, scope, staggerDelay]);

  return (
    <Tag
      className={cn("font-bold", className)}
      data-slot="text-generate-effect"
      ref={localRef}
      {...props}
    >
      <motion.span ref={scope} style={{ display: "inline" }}>
        {tokens.map((tok, idx) => (
          <motion.span
            className="opacity-0 will-change-transform will-change-opacity will-change-filter"
            key={`${tok.text}-${idx}`}
            style={{
              display: "inline-block",
              marginRight: idx === tokens.length - 1 ? 0 : "0.25em",
              filter: filter ? "blur(10px)" : "none",
              color: tok.highlight ? highlightColor : undefined,
              fontWeight: tok.highlight ? 600 : undefined,
            }}
          >
            {tok.text}
          </motion.span>
        ))}
      </motion.span>
    </Tag>
  );
});

export { TextGenerateEffect };
export default TextGenerateEffect;
